import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Building, DeviceTag } from '../../../@types/model/user';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LoadingButton } from '@mui/lab';
import { deviceState, getDevices } from 'recoil/deviceState';
import { Device } from '../../../@types/model/device';
import '../../../assets/css/deviceGroupsBuildingsDevices.scss';
import BasicModalExpanded from 'components/BasicModalExpanded';
import { DataGrid, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { buildingState, getBuildings } from 'recoil/buildingState';
import BuildingPicker from 'components/BuildingPicker';
import { FormControl, Grid } from '@mui/material';
import { InputLabel } from '@mui/material';
import DeviceGroupPicker from '../../../../src/components/DeviceGroupPicker';

interface SelectDevicesModalProps {
  //   buildingCreateDevice: Building | null;
  setSelectedDevices: (guids: string[]) => void;
  selectedDevices: string[];
  selectDevicesModal: boolean;
  setSelectDevicesModal: (selectDevicesModal: boolean) => void;
}

const SelectDevicesModal = (props: SelectDevicesModalProps) => {
  const { t } = useTranslation();
  const [devicesFiltered, setDevicesFiltered] = useState<Device[]>([]);
  const [deviceGroup, setDeviceGroup] = useState<string>('');
  const [building, setBuilding] = useState<string>('');
  const { deviceTags } = useSelector((state: RootState) => state.user);

  const buildings: Building[] = useRecoilValue(buildingState);
  const setBuildings = useSetRecoilState(buildingState);
  const setDevices = useSetRecoilState(deviceState);
  const devices: Device[] = useRecoilValue(deviceState);

  useEffect(() => {
    setDevicesFiltered(devices);
    getBuildings().then((buildings: Building[]) => {
      setBuildings(buildings);
    });
  }, []);

  useEffect(() => {
    setDevicesFiltered(
      devices.filter((d: Device) => {
        if (deviceGroup !== '') {
          if (!d.attributes.tags.includes(deviceGroup)) return false;
        }
        if (building !== '') {
          if (!d.attributes.tags.includes(building)) return false;
        }
        return true;
      })
    );
  }, [deviceGroup, building]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('name'), width: 200 },
    {
      field: 'deviceGroups',
      headerName: t('device_group', { ns: 'custom' }),
      sortable: false,
      width: 400,
      valueGetter: (params: GridValueGetterParams) => {
        let deviceGroups: string = '';
        if (params.row.deviceGroups) {
          deviceGroups = params.row.deviceGroups
            .filter((guid: string) => {
              return deviceTags.some((dt: DeviceTag) => {
                if (dt.guid === guid) return true;
                return false;
              });
            })
            .map((guid: string) => {
              return deviceTags.find((dt: DeviceTag) => {
                if (dt.guid === guid) return true;
                return false;
              })?.attributes.name;
            })
            .join(', ');
        }
        return deviceGroups;
      }
    },
    {
      field: 'buildings',
      headerName: t('building'),
      sortable: false,
      width: 400,
      valueGetter: (params: GridValueGetterParams) => {
        let buildingName: string = '';
        if (params.row.deviceGroups) {
          buildingName = params.row.deviceGroups
            .filter((guid: string) => {
              return buildings.some((b: Building) => {
                if (b.guid === guid) return true;
                return false;
              });
            })
            .map((guid: string) => {
              return buildings.find((b: Building) => {
                if (b.guid === guid) return true;
                return false;
              })?.attributes.name;
            })
            .join(', ');
        }
        return buildingName;
      }
    }
  ];

  return (
    <BasicModalExpanded
      open={props.selectDevicesModal}
      handleClose={() => {
        props.setSelectDevicesModal(false);
      }}
      style={{
        width: 'calc(100% - 50px)',
        maxHeight: 'calc(100% - 50px)'
      }}
    >
      <h2 style={{ marginBottom: '10px' }}>{t('select_devices')}</h2>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <DeviceGroupPicker
            deviceGroupGuid={deviceGroup}
            changeHandler={(guid) => setDeviceGroup(guid ?? '')}
            deviceTags={[]}
          />
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">{t('building')}</InputLabel>
            <BuildingPicker
              buildingGuid={building}
              changeHandler={(guid) => setBuilding(guid ?? '')}
              addEmptyValue={true}
            />
          </FormControl>
        </Grid>
      </Grid>

      <DataGrid
        rows={devicesFiltered.map((df: Device) => {
          return {
            id: df.guid,
            name: df.attributes.name,
            deviceGroups: df.attributes.tags,
            buildings: df.attributes.tags
          };
        })}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5]}
        checkboxSelection
        disableColumnFilter
        style={{ minHeight: '400px' }}
        onSelectionModelChange={(ids) => {
          props.setSelectedDevices(ids as string[]);
        }}
        selectionModel={props.selectedDevices}
      />

      <LoadingButton
        onClick={() => {
          props.setSelectDevicesModal(false);
        }}
        variant={'contained'}
        sx={{ marginTop: '25px' }}
      >
        {t('select_devices')}
      </LoadingButton>
    </BasicModalExpanded>
  );
};

export default SelectDevicesModal;
